if (!customElements.get('product-artwork')) {
  class ProductArtwork extends HTMLElement {
    constructor() {
      super();
      this.state = {
        isZoomed: false,
        zoomScale: 1,
        currentIndex: 0,
        isPanning: false,
        startX: 0,
        startY: 0,
        scrollLeft: 0,
        scrollTop: 0
      };

      this.selectors = {
        mainImage: '.product-artwork__main-image',
        mainImageWrapper: '.product-artwork__main-image-wrapper',
        thumbnails: '.product-artwork__thumbnail',
        counter: '.product-artwork__counter',
        prevButton: '.product-artwork__nav-prev',
        nextButton: '.product-artwork__nav-next',
        closeButton: '.product-artwork__close',
        shareButton: '.product-artwork__action--share',
        modalClose: '.product-artwork__share-close',
        modalBackdrop: '.product-artwork__share-backdrop',
        modalCopy: '.product-artwork__share-copy',
        modalInput: '.product-artwork__share-input',
        modalInstagram: '[data-instagram-share]',
        inquiryOpen: '[data-inquiry-open]',
        inquiryClose: '[data-inquiry-close]',
        inquiryModal: '.product-artwork__inquiry-modal',
        inquiryForm: '.product-artwork__inquiry-form'
      };
    }

    connectedCallback() {
      this.init();
    }



    init() {
      this.mainImage = this.querySelector(this.selectors.mainImage);
      this.mainImageWrapper = this.querySelector(this.selectors.mainImageWrapper);
      this.thumbnails = Array.from(this.querySelectorAll(this.selectors.thumbnails));
      this.counter = this.querySelector(this.selectors.counter);
      this.closeButton = this.querySelector(this.selectors.closeButton);
      
      // Events
      this.thumbnails.forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.switchImage(index));
      });

      this.mainImageWrapper.addEventListener('click', (e) => {
        if (!this.state.isZoomed) {
          this.toggleZoom(true);
        } else if (e.target === this.mainImageWrapper || e.target === this.mainImage) {
          // Only zoom out if scale is 1, otherwise it might be a pan attempt
          if (this.state.zoomScale === 1) {
            this.toggleZoom(false);
          }
        }
      });

      if (this.closeButton) {
        this.closeButton.addEventListener('click', () => this.toggleZoom(false));
      }

      this.querySelector(this.selectors.prevButton)?.addEventListener('click', () => this.navigate(-1));
      this.querySelector(this.selectors.nextButton)?.addEventListener('click', () => this.navigate(1));
      this.querySelector(this.selectors.shareButton)?.addEventListener('click', (e) => this.handleShare(e));
      this.querySelector(this.selectors.modalClose)?.addEventListener('click', () => this.closeModal());
      this.querySelector(this.selectors.modalBackdrop)?.addEventListener('click', () => this.closeModal());
      this.querySelector(this.selectors.modalCopy)?.addEventListener('click', (e) => this.copyLink(e));
      this.querySelector(this.selectors.modalInstagram)?.addEventListener('click', (e) => this.shareToInstagram(e));

      // Inquiry Modal Listeners
      this.querySelector(this.selectors.inquiryOpen)?.addEventListener('click', () => this.openInquiry());
      this.querySelectorAll(this.selectors.inquiryClose)?.forEach((btn) => btn.addEventListener('click', () => this.closeInquiry()));
      this.initInquiryForm();


      this.mainImageWrapper.addEventListener('wheel', (e) => this.handleWheel(e), { passive: false });

      // Panning
      this.mainImageWrapper.addEventListener('mousedown', (e) => this.startPanning(e));
      this.mainImageWrapper.addEventListener('mouseenter', () => this.toggleIndicator(true));
      this.mainImageWrapper.addEventListener('mouseleave', () => this.toggleIndicator(false));
      this.mainImageWrapper.addEventListener('mousemove', (e) => this.updateIndicator(e));
      window.addEventListener('mousemove', (e) => this.pan(e));
      window.addEventListener('mouseup', () => this.stopPanning());

      // Keyboard
      window.addEventListener('keydown', (e) => this.handleKeyboard(e));
    }

    switchImage(index) {
      if (index < 0 || index >= this.thumbnails.length) return;
      
      this.state.currentIndex = index;
      const selectedThumb = this.thumbnails[index];
      const newSrc = selectedThumb.dataset.src;
      const newSrcset = selectedThumb.dataset.srcset;

      this.mainImage.classList.add('is-loading');
      
      this.mainImage.src = newSrc;
      if (newSrcset) this.mainImage.srcset = newSrcset;

      this.thumbnails.forEach(t => t.classList.remove('is-active'));
      selectedThumb.classList.add('is-active');

      this.updateUI();
      
      this.mainImage.onload = () => {
        this.mainImage.classList.remove('is-loading');
        this.resetInternalZoom();
      };
    }

    navigate(direction) {
      this.switchImage(this.state.currentIndex + direction);
    }

    updateUI() {
      if (this.counter) {
        this.counter.textContent = `${this.state.currentIndex + 1} / ${this.thumbnails.length}`;
      }
      
      const prevBtn = this.querySelector(this.selectors.prevButton);
      const nextBtn = this.querySelector(this.selectors.nextButton);
      
      if (prevBtn) prevBtn.disabled = this.state.currentIndex === 0;
      if (nextBtn) nextBtn.disabled = this.state.currentIndex === this.thumbnails.length - 1;
    }

    toggleZoom(force) {
      this.state.isZoomed = force !== undefined ? force : !this.state.isZoomed;
      this.classList.toggle('is-zoomed', this.state.isZoomed);
      
      const indicator = this.querySelector('.product-artwork__zoom-indicator');
      if (indicator) {
        indicator.textContent = '+';
      }

      if (this.state.isZoomed) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
        this.resetInternalZoom();
      }
    }

    handleWheel(e) {
      if (!this.state.isZoomed) return;
      e.preventDefault();

      const zoomSpeed = 0.1;
      const delta = e.deltaY > 0 ? -1 : 1;
      let newScale = this.state.zoomScale + delta * zoomSpeed;

      // Constrain scale
      newScale = Math.min(Math.max(1, newScale), 5);

      this.state.zoomScale = newScale;
      this.applyZoom();
    }

    resetInternalZoom() {
      this.state.zoomScale = 1;
      this.applyZoom();
      this.mainImageWrapper.scrollLeft = 0;
      this.mainImageWrapper.scrollTop = 0;
    }

    applyZoom() {
      this.mainImage.style.transform = `scale(${this.state.zoomScale})`;
      // Always set cursor to none to rely on custom indicator
      this.mainImageWrapper.style.cursor = 'none';
      this.mainImage.style.cursor = 'none';
    }

    startPanning(e) {
      if (!this.state.isZoomed || this.state.zoomScale <= 1) return;
      this.state.isPanning = true;
      this.mainImageWrapper.style.cursor = 'grabbing';
      this.state.startX = e.pageX - this.mainImageWrapper.offsetLeft;
      this.state.startY = e.pageY - this.mainImageWrapper.offsetTop;
      this.state.scrollLeft = this.mainImageWrapper.scrollLeft;
      this.state.scrollTop = this.mainImageWrapper.scrollTop;
    }

    pan(e) {
      if (!this.state.isPanning) return;
      e.preventDefault();
      const x = e.pageX - this.mainImageWrapper.offsetLeft;
      const y = e.pageY - this.mainImageWrapper.offsetTop;
      const walkX = (x - this.state.startX) * 2;
      const walkY = (y - this.state.startY) * 2;
      this.mainImageWrapper.scrollLeft = this.state.scrollLeft - walkX;
      this.mainImageWrapper.scrollTop = this.state.scrollTop - walkY;
    }

    stopPanning() {
      this.state.isPanning = false;
      if (this.state.isZoomed && this.state.zoomScale > 1) {
        this.mainImageWrapper.style.cursor = 'grab';
      }
    }

    toggleIndicator(show) {
      if (!this.zoomIndicator) {
        this.zoomIndicator = this.querySelector('.product-artwork__zoom-indicator');
      }
      if (!this.zoomIndicator) return;
      
      this.zoomIndicator.style.opacity = show ? '1' : '0';
    }

    updateIndicator(e) {
      if (!this.zoomIndicator) {
        this.zoomIndicator = this.querySelector('.product-artwork__zoom-indicator');
      }
      if (!this.zoomIndicator || !this.mainImage) return;

      // Use mainImage for bounds check (the artwork itself, not the wall)
      const rect = this.mainImage.getBoundingClientRect();
      const wrapperRect = this.mainImageWrapper.getBoundingClientRect();
      
      const xRelToWrapper = e.clientX - wrapperRect.left;
      const yRelToWrapper = e.clientY - wrapperRect.top;

      this.zoomIndicator.style.left = `${xRelToWrapper}px`;
      this.zoomIndicator.style.top = `${yRelToWrapper}px`;

      // Check if mouse is over the actual image
      const isOverImage = e.clientX >= rect.left && e.clientX <= rect.right && 
                          e.clientY >= rect.top && e.clientY <= rect.bottom;

      // Hide indicator if panning or not over image
      if (this.state.isPanning || (this.state.isZoomed && this.state.zoomScale > 1) || !isOverImage) {
        this.zoomIndicator.style.opacity = '0';
      } else {
        this.zoomIndicator.style.opacity = '1';
        this.zoomIndicator.textContent = '+';
      }
    }

    handleKeyboard(e) {
      if (e.key === 'Escape' && this.state.isZoomed) {
        this.toggleZoom(false);
      }
      if (e.key === 'ArrowRight') {
        this.navigate(1);
      }
      if (e.key === 'ArrowLeft') {
        this.navigate(-1);
      }
    }

    async handleShare(e) {
      this.classList.add('is-modal-open');
    }

    closeModal() {
      this.classList.remove('is-modal-open');
    }

    async copyLink(e) {
      const btn = e.currentTarget;
      const input = this.querySelector(this.selectors.modalInput);
      const url = input.value;

      try {
        await navigator.clipboard.writeText(url);
        const originalText = btn.textContent;
        btn.textContent = 'Copied!';
        btn.style.backgroundColor = '#222';
        btn.style.color = '#fff';
        
        setTimeout(() => {
          btn.textContent = originalText;
          btn.style.backgroundColor = '';
          btn.style.color = '';
        }, 2000);
      } catch (err) {
        console.error('Could not copy text: ', err);
      }
    }

    async shareToInstagram(e) {
      const button = e.currentTarget;
      const shareUrl = button.getAttribute('data-share-url') || window.location.href;

      try {
        await navigator.clipboard.writeText(shareUrl);
        const label = button.querySelector('span');
        const originalText = button.dataset.originalLabel || label?.textContent || 'Instagram';
        button.dataset.originalLabel = originalText;

        if (label) label.textContent = 'Link copied';
        button.style.opacity = '0.6';

        setTimeout(() => {
          if (label) label.textContent = originalText;
          button.style.opacity = '';
        }, 2000);
      } catch (error) {
        console.error('Could not prepare Instagram share: ', error);
      }
    }

    openInquiry() {
      this.classList.remove('is-modal-open');
      this.classList.add('is-inquiry-open');
    }

    closeInquiry() {
      this.classList.remove('is-inquiry-open');
    }

    initInquiryForm() {
      const form = this.querySelector(this.selectors.inquiryForm);
      if (!form) return;

      const successEl = this.querySelector('.product-artwork__inquiry-success');
      const errorEl = this.querySelector('.product-artwork__inquiry-error');
      const submitBtn = form.querySelector('.product-artwork__inquiry-submit');

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (errorEl) errorEl.style.display = 'none';

        const originalBtnText = submitBtn ? submitBtn.textContent : 'SUBMIT';
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = 'SENDING...';
        }

        try {
          const formData = new FormData(form);

          // Embed artwork image as HTML <img> tag inside the body field,
          // so Shopify's notification email may render it directly.
          const imageInput = form.querySelector('input[name="contact[Artwork Image]"]');
          if (imageInput && imageInput.value) {
            // Ensure absolute https: URL (Shopify CDN often returns protocol-relative //)
            let imgUrl = imageInput.value.trim();
            if (imgUrl.startsWith('//')) imgUrl = 'https:' + imgUrl;

            const bodyField = formData.get('contact[body]') || '';
            const imgTag = '\n\n<img src="' + imgUrl + '" alt="Artwork" style="max-width:320px;height:auto;border-radius:6px;border:1px solid #eee;" />';
            formData.set('contact[body]', bodyField + imgTag);

            // Also fix hidden field to absolute URL
            imageInput.value = imgUrl;
            formData.set('contact[Artwork Image]', imgUrl);
          }

          const response = await fetch(form.getAttribute('action') || '/contact', {
            method: 'POST',
            body: formData,
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              Accept: 'text/html'
            }
          });

          // Show success message inline inside modal
          if (successEl) successEl.style.display = 'block';
          form.querySelectorAll('.product-artwork__inquiry-field, .product-artwork__inquiry-submit-row, .product-artwork__inquiry-legal').forEach((el) => {
            el.style.display = 'none';
          });
        } catch (err) {
          console.error('Inquiry submission error:', err);
          if (errorEl) {
            errorEl.style.display = 'block';
            errorEl.textContent = 'Failed to submit inquiry. Please try again or email us directly.';
          }
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalBtnText;
          }
        }
      });
    }

    disconnectedCallback() {
      // cleanup
    }
  }

  customElements.define('product-artwork', ProductArtwork);
}
